import { forwardRef, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { ButtonGroup } from '@unifyapps/ui/components/ButtonGroup';
import { Button as BaseButton } from '@unifyapps/ui/components/Button';
import type { Option } from '@unifyapps/defs/blocks/ButtonGroup/types';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import type {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
} from '@unifyapps/defs/types/blocks/button';
import { getButtonGroupStyles } from '@unifyapps/ui/components/Button/styles';

type Props = {
  color?: ButtonColors;
  size?: ButtonSizes;
  variant?: ButtonVariants;
  className?: string;
  style?: CSSProperties;
  attributesAndEventListeners: Record<string, unknown> | undefined;
  options: Option[];
  onAction: (id: string, label: string) => void;
  selectedOptionId?: string;
};

function Button({
  color,
  variant,
  option,
  index,
  size,
  numberOfOptions,
  tooltipContent,
  onAction,
  ...rest
}: Pick<Props, 'color' | 'size' | 'variant' | 'onAction'> & {
  option: Option;
  index: number;
  numberOfOptions: number;
  tooltipContent: string | undefined;
}) {
  const onClick = useCallback(() => {
    onAction(option.id, option.label);
  }, [onAction, option.id, option.label]);

  const style = useMemo(
    () =>
      getButtonGroupStyles({
        index,
        numberOfOptions,
      }),
    [index, numberOfOptions],
  );

  return tooltipContent ? (
    <Tooltip key={option.id} placement="top" title={tooltipContent}>
      <BaseButton
        className="grow !shadow-none"
        color={color ?? 'brand'}
        disabled={option.appearance?.disabled}
        endDecoratorComponent={getIconFromRegistry(option.appearance?.endDecorator)}
        loading={option.appearance?.loading}
        onClick={onClick}
        size={size ?? 'md'}
        startDecoratorComponent={getIconFromRegistry(option.appearance?.startDecorator)}
        style={style}
        variant={variant ?? 'solid'}
        {...rest}
      >
        {option.label}
      </BaseButton>
    </Tooltip>
  ) : (
    <BaseButton
      className="grow !shadow-none"
      color={color ?? 'brand'}
      disabled={option.appearance?.disabled}
      endDecoratorComponent={getIconFromRegistry(option.appearance?.endDecorator)}
      loading={option.appearance?.loading}
      onClick={onClick}
      size={size ?? 'md'}
      startDecoratorComponent={getIconFromRegistry(option.appearance?.startDecorator)}
      style={style}
      variant={variant ?? 'solid'}
      {...rest}
    >
      {option.label}
    </BaseButton>
  );
}

export const DefaultButtonGroup = forwardRef<HTMLDivElement, Props>(
  function DefaultButtonGroup(props, ref) {
    const {
      size,
      color,
      variant,
      className,
      style,
      options,
      attributesAndEventListeners,
      onAction,
      selectedOptionId,
    } = props;

    const slotProps = useMemo(
      () => ({ root: attributesAndEventListeners }),
      [attributesAndEventListeners],
    );

    const restButtonProps = useMemo(
      () => (selectedOptionId ? { 'data-btn-type': 'toggle' } : {}),
      [selectedOptionId],
    );

    return (
      <ButtonGroup className={className} ref={ref} slotProps={slotProps} style={style}>
        {options.map((option, index) => {
          const tooltipContent = option.addOns?.tooltip ? option.addOns.tooltip.content : undefined;

          return (
            <Button
              color={color}
              data-selected={selectedOptionId === option.id}
              index={index}
              key={option.id}
              numberOfOptions={options.length}
              onAction={onAction}
              option={option}
              size={size}
              tooltipContent={tooltipContent}
              variant={variant}
              {...restButtonProps}
            />
          );
        })}
      </ButtonGroup>
    );
  },
);
